// ApprovalSettings.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../components/ui/button";
import { useAccess } from "../contexts/AccessContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  CheckCircle,
  Users,
  Clock,
  Search,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  FileText,
  RefreshCw,
  Loader2,
  Download,
  MessageSquare,
  Upload,
  RotateCcw,
} from "lucide-react";
import { BankExportDialog, transferSheetFilename } from "../components/BankExportDialog";
import { BankStatementUploadDialog } from "../components/BankStatementUploadDialog";
import { salaryApi, type SalaryPayment, type StatementApplyResult } from "../api/salary";
import { formatIsoDate, localIsoDate, paymentBucketFor } from "../utils/salaryPayment";
import { downloadSalarySlip, viewSalarySlip } from "../api/employees";
import FilterDropdown from "../components/FilterDropdown";
import PersonAvatar from "../components/people/PersonAvatar";
import { approvalBucketFor, getPreviousPayrollPeriod } from "./payrollApprovalLogic";
import type { ApprovalBucket } from "./payrollApprovalLogic";

// Types
interface SalaryData {
  salarytID: string;
  emptID: string;
  employeeID: string;
  name: string;
  calculationType: string;
  mode: string;
  status: string;
  approved: boolean;
  sent_for_approval?: boolean;
  department: string;
  designation: string;
  joiningDate: string;
  SalaryDate: string;
  month: number;
  year: number;
  monthlySalary: number;
  bonus: number;
  extrapay: number;
  netSalary: number;
  approved_by?: string;
  approved_at?: string;
  payment?: SalaryPayment | null;
}

interface ApiResponse {
  success: boolean;
  message: string;
  year: number;
  month: number;
  count: number;
  results: Array<{
    emptID: string;
    success: boolean;
    data: SalaryData;
  }>;
}

interface SalaryRecord extends SalaryData {
  salarytID: string;
  emptID: string;
  employeeID: string;
  name: string;
  calculationType: string;
  mode: string;
  status: string;
  approved: boolean;
  sent_for_approval?: boolean;
  department: string;
  designation: string;
  joiningDate: string;
  SalaryDate: string;
  month: number;
  year: number;
  monthlySalary: number;
  bonus: number;
  extrapay: number;
  netSalary: number;
  approved_by?: string;
  approved_at?: string;
  payment?: SalaryPayment | null;
}


// Helper Components
const StatusBadge = ({ bucket }: { bucket: ApprovalBucket }) => {
  if (bucket === "approved") {
    return (
      <span
        className="inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold"
        style={{ backgroundColor: "#e8f5ed", borderColor: "#bbf7d0", color: "#14532d" }}
      >
        <CheckCircle className="w-3 h-3 mr-1" />
        Approved
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold"
      style={{ backgroundColor: "#fff7ed", borderColor: "#fed7aa", color: "#9a3412" }}
    >
      <Clock className="w-3 h-3 mr-1" />
      Pending
    </span>
  );
};

// Payment column on the Approved tab — set by Upload Bank Statement or a manual mark.
const PaymentBadge = ({ payment }: { payment?: SalaryPayment | null }) => {
  const bucket = paymentBucketFor({ payment });
  if (bucket === "paid") {
    return (
      <div className="min-w-0">
        <span
          className="inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold"
          style={{ backgroundColor: "#e8f5ed", borderColor: "#bbf7d0", color: "#14532d" }}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Paid
        </span>
        <div className="mt-0.5 text-[11px] leading-tight text-gray-500">
          <div>{formatIsoDate(payment?.txn_date)}</div>
          {payment?.utr ? (
            <div className="break-all" title={payment.utr}>{payment.utr}</div>
          ) : payment?.source === "manual" ? (
            <div>Marked by hand</div>
          ) : null}
        </div>
      </div>
    );
  }
  if (bucket === "returned") {
    return (
      <div className="min-w-0">
        <span
          className="inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold"
          style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}
        >
          <AlertCircle className="w-3 h-3 mr-1" />
          Returned
        </span>
        <div className="mt-0.5 text-[11px] text-red-700">{payment?.return_reason || "Transfer came back"}</div>
      </div>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold"
      style={{ backgroundColor: "#f3f4f6", borderColor: "#e5e7eb", color: "#4b5563" }}
    >
      <Clock className="w-3 h-3 mr-1" />
      Unpaid
    </span>
  );
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const BUTTON_TONES = {
  primary: "bg-[#0f2d1f] !text-white hover:bg-[#123b29] hover:!text-white [&_svg]:!text-white",
  slate: "bg-[#475569] !text-white hover:bg-[#334155] hover:!text-white [&_svg]:!text-white",
  orange: "bg-[#f97316] !text-white hover:bg-[#ea580c] hover:!text-white [&_svg]:!text-white",
};

const DetailRow = ({ label, value }: { label: string; value?: string | number }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
    <div className="text-[11px] font-semibold uppercase text-gray-500">{label}</div>
    <div className="mt-0.5 text-sm font-semibold text-gray-900">{value || "—"}</div>
  </div>
);

// Main Component
const ApprovalSettings = () => {
  const [activeTab, setActiveTab] = useState<"pending" | "approved">("pending");
  // Row selection — mirrors Payroll Records. Keyed by emptID (what the approve /
  // reject APIs take). Cleared whenever the visible set changes so a hidden row
  // can never be approved by a stale tick.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Manage level gates approve/reject actions; View → read-only.
  const { hasAccess, canManage } = useAccess();
  const canApprovePayroll = !hasAccess || canManage("payroll-approval");
  const defaultPayrollPeriod = useMemo(() => getPreviousPayrollPeriod(), []);
  const [allSalaryRecords, setAllSalaryRecords] = useState<SalaryRecord[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>(defaultPayrollPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState<string>(defaultPayrollPeriod.month);
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>("");
  // Bank bulk-upload sheet needs a few per-bank values on every row that aren't
  // stored anywhere (debit account, sender address, payment method, ...). The
  // shared BankExportDialog collects them; the Expenses page uses the same one.
  const [showExportDialog, setShowExportDialog] = useState(false);
  // Upload Bank Statement marks approved salaries paid from the statement; the
  // single-row dialog marks one Paid / Unpaid by hand.
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [paymentRecord, setPaymentRecord] = useState<SalaryRecord | null>(null);
  const [paymentForm, setPaymentForm] = useState({ reference: "", paidOn: "", note: "" });
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SalaryRecord | null>(null);
  const [approvalComments, setApprovalComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));

  // Function to call export_employees API (GET)
  const callExportEmployeesAPI = useCallback(async () => {
    try {
      const response = await salaryApi.exportEmployees();
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error("Error calling export_employees:", error);
      return { success: false, error: error?.response?.data?.detail || error?.message || "Failed to export employees" };
    }
  }, []);

  // Function to call export_salary API (POST)
  const callExportSalaryAPI = useCallback(async () => {
    try {
      // Get all approved employee IDs to export
      const approvedEmployeeIds = allSalaryRecords
        .filter(record => approvalBucketFor(record) === "approved")
        .map(record => record.emptID);
      
      const response = await salaryApi.exportSalary(selectedYear, selectedMonth, approvedEmployeeIds);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error("Error calling export_salary:", error);
      return { success: false, error: error?.response?.data?.detail || error?.message || "Failed to export salary" };
    }
  }, [selectedYear, selectedMonth, allSalaryRecords]);

  // Function to handle the entire export process after approval
  const handlePostApprovalExport = useCallback(async (employeeName: string) => {
    setIsExporting(true);
    setMessage("📤 Your salary is exporting...");
    setExportProgress("Starting export process...");
    
    try {
      // First API call: export_employees (GET)
      setExportProgress("Exporting employee records...");
      setMessage("📤 Exporting employee records...");
      const employeesResult = await callExportEmployeesAPI();
      
      if (!employeesResult.success) {
        throw new Error(employeesResult.error);
      }
      setExportProgress("Employee records exported successfully!");
      
      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Second API call: export_salary (POST)
      setExportProgress("Exporting salary records...");
      setMessage("📤 Exporting salary records...");
      const salaryResult = await callExportSalaryAPI();
      
      if (!salaryResult.success) {
        throw new Error(salaryResult.error);
      }
      
      // Both exports successful
      setExportProgress("Export completed successfully!");
      setMessage(`✅ Successfully exported employee and salary data for ${employeeName}!`);
      
      // Check if there's a download URL in the response and trigger download
      if (salaryResult.data?.download_url) {
        window.open(salaryResult.data.download_url, '_blank');
      }
      if (employeesResult.data?.download_url) {
        window.open(employeesResult.data.download_url, '_blank');
      }
      
      return true;
    } catch (error: any) {
      // The salary is already approved; the export is a best-effort secondary
      // step, so don't surface its failure on the UI (still logged for
      // debugging). Keep the positive approval confirmation instead.
      console.error("Export error:", error);
      setMessage(`✅ Salary approved successfully for ${employeeName}!`);
      setExportProgress("");
      return false;
    } finally {
      setIsExporting(false);
      setTimeout(() => {
        setMessage("");
        setExportProgress("");
      }, 5000);
    }
  }, [callExportEmployeesAPI, callExportSalaryAPI]);

  // Fetch all salary records from /salary/get_approved_salary API
  const fetchSalaryRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await salaryApi.getApprovedSalaries(selectedYear, selectedMonth);
      const apiResponse = response.data as ApiResponse;
      
      if (apiResponse.success && apiResponse.results) {
        // Extract the data from nested structure
        const formattedRecords: SalaryRecord[] = apiResponse.results.map(item => ({
          salarytID: item.data.salarytID,
          emptID: item.data.emptID,
          employeeID: item.data.employeeID,
          name: item.data.name,
          calculationType: item.data.calculationType,
          mode: item.data.mode,
          status: item.data.status,
          approved: item.data.approved,
          sent_for_approval: item.data.sent_for_approval,
          department: item.data.department,
          designation: item.data.designation,
          joiningDate: item.data.joiningDate,
          SalaryDate: item.data.SalaryDate,
          month: item.data.month,
          year: item.data.year,
          monthlySalary: item.data.monthlySalary,
          bonus: item.data.bonus || 0,
          extrapay: item.data.extrapay || 0,
          netSalary: item.data.netSalary,
          approved_by: item.data.approved_by,
          approved_at: item.data.approved_at,
          payment: item.data.payment ?? null,
        }));
        
        setAllSalaryRecords(formattedRecords);
      } else {
        setAllSalaryRecords([]);
      }
    } catch (error: any) {
      console.error("Error fetching salary records:", error);
      setMessage(error?.response?.data?.detail || "Failed to fetch salary records");
      setAllSalaryRecords([]);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedYear, selectedMonth]);

  // Approve a single record with comments and then trigger exports
  const handleApproveWithComments = useCallback(async () => {
    if (!selectedRecord) return;

    setIsApproving(true);
    try {
      const employeeId = selectedRecord.emptID;
      // Call approve API with the employee ID
      await salaryApi.approveSalaries(selectedYear, selectedMonth, [employeeId]);
      
      setMessage(`✅ Successfully approved salary for ${selectedRecord.name}! Comments: ${approvalComments || "No comments"}`);
      
      // Refresh the data to get updated approved status
      await fetchSalaryRecords();
      
      // Trigger the export APIs after successful approval
      await handlePostApprovalExport(selectedRecord.name);
      
      // Reset states
      setShowApproveDialog(false);
      setSelectedRecord(null);
      setApprovalComments("");
      
      return true;
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed to approve salary");
      return false;
    } finally {
      setIsApproving(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedRecord, approvalComments, selectedYear, selectedMonth, fetchSalaryRecords, handlePostApprovalExport]);

  // Reject a single record with reason
  const handleRejectWithReason = useCallback(async () => {
    if (!selectedRecord) return;

    setIsApproving(true);
    try {
      // Call reject API (persists the reason on the record)
      await salaryApi.rejectSalaries(selectedYear, selectedMonth, [selectedRecord.emptID], rejectionReason);

      // Refresh the data
      await fetchSalaryRecords();
      
      // Reset states
      setShowRejectDialog(false);
      setSelectedRecord(null);
      setRejectionReason("");
      
      return true;
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed to reject salary");
      return false;
    } finally {
      setIsApproving(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedRecord, rejectionReason, selectedYear, selectedMonth, fetchSalaryRecords]);

  // Export approved salaries to the bank bulk-upload sheet (manual export)
  const exportApprovedSalariesToCSV = useCallback(async (bank: string, fields: Record<string, string>) => {
    const approvedRecords = allSalaryRecords.filter(record => approvalBucketFor(record) === "approved");
    if (approvedRecords.length === 0) return;

    setIsExporting(true);
    setMessage("📤 Exporting approved salaries...");

    try {
      const approvedEmployeeIds = approvedRecords.map(record => record.emptID);

      // Best-effort push to StartupKhata — don't let its failure (e.g. the
      // external API being unreachable) block the Excel download below.
      try {
        await salaryApi.exportSalary(selectedYear, selectedMonth, approvedEmployeeIds);
      } catch (pushErr) {
        console.warn("StartupKhata salary push failed; continuing to Excel export:", pushErr);
      }

      // Download the bank bulk salary-upload sheet (.xlsx) built server-side —
      // the plain SNO/AMOUNT/DATE/... column format banks expect for NEFT/RTGS
      // bulk uploads, with each employee's bank details and net payable.
      const blob = await salaryApi.exportSalaryTransferSheet(
        selectedYear,
        selectedMonth,
        approvedEmployeeIds,
        bank,
        fields
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Same name the backend puts in Content-Disposition (AU: fixed prefix +
      // upper-cased payment instructions; other banks: dated, descriptive).
      link.download = transferSheetFilename("Salary", bank, selectedYear, selectedMonth, fields);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setMessage("✅ Salary Bulk Upload sheet exported successfully!");
    } catch (error: any) {
      console.error("Export error:", error);
      setMessage("❌ Failed to export salaries");
    } finally {
      setIsExporting(false);
      setTimeout(() => setMessage(""), 3000);
    }
  }, [allSalaryRecords, selectedYear, selectedMonth]);

  const openExportDialog = useCallback(() => setShowExportDialog(true), []);

  const periodLabel = `${months.find((m) => m.value === selectedMonth)?.label ?? selectedMonth} ${selectedYear}`;

  const handleStatementApplied = useCallback(
    async (result: StatementApplyResult) => {
      const parts = [`${result.paid} marked paid`];
      if (result.returned) parts.push(`${result.returned} returned`);
      if (result.failed) parts.push(`${result.failed} skipped`);
      setMessage(`✅ Bank statement applied: ${parts.join(", ")}`);
      await fetchSalaryRecords();
      setTimeout(() => setMessage(""), 5000);
    },
    [fetchSalaryRecords]
  );

  // A paid row's button undoes the payment; any other row's records one.
  const markingUnpaid = paymentRecord ? paymentBucketFor(paymentRecord) === "paid" : false;

  const openPaymentDialog = (record: SalaryRecord) => {
    setPaymentRecord(record);
    setPaymentForm({ reference: "", paidOn: localIsoDate(), note: "" });
  };

  const savePayment = async () => {
    if (!paymentRecord) return;
    setIsSavingPayment(true);
    try {
      await salaryApi.markSalaryPayment(
        selectedYear,
        selectedMonth,
        [paymentRecord.emptID],
        markingUnpaid ? "UNPAID" : "PAID",
        markingUnpaid
          ? {}
          : {
              reference: paymentForm.reference.trim() || undefined,
              paid_on: paymentForm.paidOn || undefined,
              note: paymentForm.note.trim() || undefined,
            }
      );
      setMessage(`✅ ${paymentRecord.name} marked ${markingUnpaid ? "unpaid" : "paid"}`);
      setPaymentRecord(null);
      await fetchSalaryRecords();
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed to update payment status");
    } finally {
      setIsSavingPayment(false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  // Get pending records (approved = false)
  const getPendingRecords = () => {
    return allSalaryRecords.filter(record => approvalBucketFor(record) === "pending");
  };


  // Get approved records (approved = true)
  const getApprovedRecords = () => {
    return allSalaryRecords.filter(record => approvalBucketFor(record) === "approved");
  };

  // Get filtered data based on search query and active tab
  const getFilteredData = () => {
    const data = activeTab === "pending" ? getPendingRecords() : getApprovedRecords();
    if (!searchQuery) return data;
    
    return data.filter(emp =>
      emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const currentData = getFilteredData();

  // --- Selection + totals ---------------------------------------------------
  // Selection only exists on Pending, where it drives what gets approved. On
  // Approved there is nothing to act on, so that tab just shows the total.
  const showSelection = activeTab === "pending";
  const visibleTotal = currentData.reduce(
    (sum, r) => sum + (Number(r.netSalary) || 0),
    0
  );
  // Approved isn't paid: only salaries the bank statement (or a manual mark)
  // confirmed count towards Paid, at the amount that actually left the account.
  const paidRecords = currentData.filter((r) => paymentBucketFor(r) === "paid");
  const paidTotal = paidRecords.reduce(
    (sum, r) => sum + (Number(r.payment?.amount ?? r.netSalary) || 0),
    0
  );
  const selectableIds = currentData.map((r) => r.emptID);
  const allVisibleSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  // What actually gets paid for the ticked rows — the same Net Salary column the
  // table shows, so the figure always reconciles with what's on screen.
  const selectedTotal = currentData
    .filter((r) => selectedIds.has(r.emptID))
    .reduce((sum, r) => sum + (Number(r.netSalary) || 0), 0);

  const toggleRow = (emptID: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(emptID)) next.delete(emptID);
      else next.add(emptID);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) selectableIds.forEach((id) => next.delete(id));
      else selectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const pendingCount = getPendingRecords().length;
  const approvedCount = getApprovedRecords().length;
  const totalCount = allSalaryRecords.length;

  // Open approve dialog
  const openApproveDialog = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setApprovalComments("");
    setShowApproveDialog(true);
  };

  // Open reject dialog
  const openRejectDialog = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setRejectionReason("");
    setShowRejectDialog(true);
  };

  const openRevertDialog = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setShowRevertDialog(true);
  };

  const handleRevertToPending = useCallback(async () => {
    if (!selectedRecord) return;

    setIsApproving(true);
    try {
      const response = await salaryApi.revertApprovedSalaries(
        selectedYear,
        selectedMonth,
        [selectedRecord.emptID]
      );
      // The endpoint answers 200 even when it refuses the row — e.g. a salary
      // already marked paid — so read the row's own result before claiming success.
      const rows = response?.data?.results as unknown as
        | Array<{ success?: boolean; message?: string }>
        | undefined;
      const refused = rows?.find((row) => row?.success === false);
      if (refused) {
        setMessage(`❌ ${refused.message || "Salary could not be moved back to pending"}`);
        setShowRevertDialog(false);
        setSelectedRecord(null);
        return;
      }
      setMessage(`✅ Salary for ${selectedRecord.name} moved back to pending`);
      setShowRevertDialog(false);
      setSelectedRecord(null);
      await fetchSalaryRecords();
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed to move salary back to pending");
    } finally {
      setIsApproving(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedRecord, selectedYear, selectedMonth, fetchSalaryRecords]);

  const handleViewDetails = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setShowDetailsDialog(true);
  };

  const handlePreviewSalarySlip = async (record: SalaryRecord) => {
    try {
      await viewSalarySlip(record.emptID, Number(record.month), Number(record.year));
    } catch (error: any) {
      setMessage(error?.message || "❌ Failed to open salary-slip PDF");
    }
  };

  const handleDownloadSalarySlip = async (record: SalaryRecord) => {
    try {
      await downloadSalarySlip(record.emptID, Number(record.month), Number(record.year));
    } catch (error: any) {
      setMessage(error?.message || "❌ Failed to download salary-slip PDF");
    }
  };

  const handleApproveAll = useCallback(async () => {
  const pendingRecords = getPendingRecords();

  if (pendingRecords.length === 0) {
    setMessage("⚠️ No pending records to approve");
    return;
  }

  setIsApproving(true);

  try {
    // Tick some rows → approve exactly those; tick nothing → approve every
    // pending record, the button's original behaviour.
    const picked = pendingRecords.filter(r => selectedIds.has(r.emptID));
    const ids = (picked.length ? picked : pendingRecords).map(r => r.emptID);

    await salaryApi.approveSalaries(selectedYear, selectedMonth, ids);

    setMessage(`✅ Successfully approved ${ids.length} records`);

    await fetchSalaryRecords();

    // optional export
    await handlePostApprovalExport("All Employees");

  } catch (error: any) {
    setMessage(error?.response?.data?.detail || "❌ Failed to approve all");
  } finally {
    setIsApproving(false);
    setTimeout(() => setMessage(""), 5000);
  }
}, [allSalaryRecords, selectedYear, selectedMonth, selectedIds]);

  // Fetch data when year or month changes
  useEffect(() => {
    fetchSalaryRecords();
  }, [selectedYear, selectedMonth, fetchSalaryRecords]);

  // Switching tab / period / search changes which rows exist, so drop the
  // selection rather than carry ticks for rows nobody can see.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, selectedYear, selectedMonth, searchQuery]);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Payroll Approval
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Review and approve employee salary records</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          {activeTab === "approved" && approvedCount > 0 && (
            <Button
              onClick={openExportDialog}
              disabled={isExporting}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 ${BUTTON_TONES.primary}`}
            >
              {isExporting ? (
                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              )}
              Export Excel
            </Button>
          )}

          {canApprovePayroll && activeTab === "approved" && approvedCount > 0 && (
            <Button
              onClick={() => setShowStatementDialog(true)}
              disabled={isExporting}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 ${BUTTON_TONES.slate}`}
            >
              <Upload className="w-3 h-3 sm:w-4 sm:h-4" />
              Upload Bank Statement
            </Button>
          )}

          {canApprovePayroll && activeTab === "pending" && pendingCount > 0 && (
  <Button
    onClick={handleApproveAll}
    disabled={isApproving || isExporting}
    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2 ${BUTTON_TONES.primary}`}
  >
    {isApproving ? (
      <Loader2 className="w-4 h-4 animate-spin" />
    ) : (
      <CheckCircle className="w-4 h-4" />
    )}
    {selectedCount > 0
      ? `Approve Selected (${selectedCount}) · ${formatCurrency(selectedTotal)}`
      : `Approve All (${pendingCount})`}
  </Button>
)}
          </div>
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div
          className={`m-4 p-3 rounded-lg shadow-md border-l-4 text-sm ${
            message.includes("✅") || message.includes("successfully")
              ? "bg-green-50 border-green-500 text-green-800"
              : message.includes("⚠️")
              ? "bg-yellow-50 border-yellow-500 text-yellow-800"
              : "bg-red-50 border-red-500 text-red-800"
          }`}
        >
          <div className="flex items-center gap-2">
            {message.includes("✅") ? (
              <span className="text-lg">✅</span>
            ) : message.includes("❌") ? (
              <span className="text-lg">❌</span>
            ) : message.includes("⚠️") ? (
              <span className="text-lg">⚠️</span>
            ) : message.includes("📤") ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : null}
            <p className="font-medium">{message}</p>
          </div>
        </div>
      )}

      {/* Export Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Exporting Data</h3>
              <p className="text-sm text-gray-600 text-center">
                {exportProgress || "Your salary data is being exported. Please wait..."}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact control bar: segmented tabs (with live counts) + inline filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-3 py-2.5 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          {/* Segmented tabs with counts (replaces the separate stat cards + tab bar) */}
          <div className="inline-flex bg-gray-100 rounded-lg p-1 self-start">
            <button
              onClick={() => { setActiveTab("pending"); setSearchQuery(""); }}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === "pending" ? "bg-white shadow-sm text-amber-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Clock className="w-4 h-4" /> Pending
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === "pending" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>{pendingCount}</span>
            </button>
            <button
              onClick={() => { setActiveTab("approved"); setSearchQuery(""); }}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === "approved" ? "bg-white shadow-sm text-emerald-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <CheckCircle className="w-4 h-4" /> Approved
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>{approvedCount}</span>
            </button>
          </div>

          <span className="hidden lg:block text-xs text-gray-400">{totalCount} total</span>
          <div className="hidden lg:block flex-1" />

          {/* Inline filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-28">
              <FilterDropdown value={selectedYear} onChange={setSelectedYear} searchable={false} ariaLabel="Filter by year" options={years} />
            </div>
            <div className="w-32">
              <FilterDropdown value={selectedMonth} onChange={setSelectedMonth} searchable={false} ariaLabel="Filter by month" options={months} />
            </div>
            <div className="relative flex-1 min-w-[170px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <button onClick={fetchSalaryRecords} disabled={isLoading} title="Refresh" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Results caption + selection toolbar */}
      <div className="mb-2 px-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500">
            Showing <strong className="text-gray-700">{currentData.length}</strong> {activeTab === "pending" ? "pending" : "approved"} record{currentData.length === 1 ? "" : "s"}
          </span>
        </div>
        {currentData.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {showSelection ? (
              <>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {allVisibleSelected ? "Deselect All" : "Select All"}
                </button>
                <span className="text-xs text-gray-500">{selectedCount} selected</span>
                {selectedCount > 0 && (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                    Payable: {formatCurrency(selectedTotal)}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                  Paid: {formatCurrency(paidTotal)} ({paidRecords.length}/{currentData.length})
                </span>
                <span className="text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                  Total approved: {formatCurrency(visibleTotal)}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Records Table — dense, full-width (uses the space regardless of count) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="flex items-center justify-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-14">
            <div className="flex flex-col items-center gap-2">
              <Users className="w-12 h-12 text-gray-300" />
              <p className="text-sm font-medium text-gray-500">No records found</p>
              <p className="text-xs text-gray-400">
                {activeTab === "pending"
                  ? "No pending salary approvals for the selected period"
                  : "No approved salary records for the selected period"}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 80px)" }}>
            {/* Scroll container: page scrolls normally until the table header reaches
                the top, then the header pins and only the body scrolls internally. */}
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20">
                <tr className="bg-gray-50 text-left shadow-[inset_0_-1px_0_0_#e5e7eb]">
                  {showSelection && (
                    <th className="bg-gray-50 px-3 py-2.5 w-10">
                      <input
                        type="checkbox"
                        aria-label={allVisibleSelected ? "Deselect all rows" : "Select all rows"}
                        checked={allVisibleSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 cursor-pointer accent-emerald-600"
                      />
                    </th>
                  )}
                  <th className="bg-gray-50 px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">Employee</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">Designation</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide text-right">Monthly</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide text-right">Bonus</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide text-right">Extra Pay</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide text-right">Net Salary</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">Period</th>
                  <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">Status</th>
                  {activeTab === "approved" && (
                    <th className="bg-gray-50 px-3 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">Payment</th>
                  )}
                  <th className="bg-gray-50 px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentData.map((record) => (
                  <tr
                    key={record.salarytID}
                    className={`transition-colors ${
                      showSelection && selectedIds.has(record.emptID)
                        ? "bg-emerald-50/40"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {showSelection && (
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          aria-label={`Select ${record.name}`}
                          checked={selectedIds.has(record.emptID)}
                          onChange={() => toggleRow(record.emptID)}
                          className="h-4 w-4 cursor-pointer accent-emerald-600"
                        />
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar
                          employeeId={record.emptID}
                          employeeCode={record.employeeID}
                          name={record.name}
                          size={32}
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{record.name}</div>
                          <div className="text-xs text-gray-500">ID: {record.employeeID}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600">{record.designation || "—"}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700 whitespace-nowrap">{formatCurrency(record.monthlySalary)}</td>
                    <td className="px-3 py-2.5 text-right text-amber-600 whitespace-nowrap">{formatCurrency(record.bonus || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-blue-600 whitespace-nowrap">{formatCurrency(record.extrapay || 0)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <span className="font-bold text-emerald-600">{formatCurrency(record.netSalary)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {months.find(m => m.value === record.month?.toString().padStart(2, "0"))?.label} {record.year}
                    </td>
                    <td className="px-3 py-2.5"><StatusBadge bucket={approvalBucketFor(record)} /></td>
                    {activeTab === "approved" && (
                      <td className="px-3 py-2.5 max-w-[200px]">
                        <PaymentBadge payment={record.payment} />
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-center gap-2">
                        {canApprovePayroll && (
                          <>
                            <Button
                              type="button"
                              aria-label={`View Salary Slip PDF for ${record.name}`}
                              title="View Salary Slip PDF"
                              onClick={() => handlePreviewSalarySlip(record)}
                              className={`px-2.5 py-1.5 rounded-lg ${BUTTON_TONES.slate}`}
                            >
                              <FileText className="w-3.5 h-3.5" /> PDF
                            </Button>
                            <Button
                              type="button"
                              aria-label={`Download Salary Slip PDF for ${record.name}`}
                              title="Download Salary Slip PDF"
                              onClick={() => handleDownloadSalarySlip(record)}
                              className={`px-2.5 py-1.5 rounded-lg ${BUTTON_TONES.slate}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {canApprovePayroll && activeTab === "pending" && approvalBucketFor(record) === "pending" ? (
                          <>
                          <Button
                            onClick={() => openApproveDialog(record)}
                            disabled={isExporting}
                            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${BUTTON_TONES.primary}`}
                          >
                            <ThumbsUp className="w-3.5 h-3.5" /> Approve
                          </Button>
                          <Button
                            onClick={() => openRejectDialog(record)}
                            disabled={isExporting}
                            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${BUTTON_TONES.orange}`}
                          >
                            <ThumbsDown className="w-3.5 h-3.5" /> Reject
                          </Button>
                          </>
                        ) : activeTab === "approved" ? (
                          <>
                          <Button
                            onClick={() => handleViewDetails(record)}
                            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${BUTTON_TONES.slate}`}
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </Button>
                          {canApprovePayroll && (
                            <Button
                              onClick={() => openPaymentDialog(record)}
                              className={`text-xs px-3 py-1.5 rounded-lg whitespace-nowrap ${
                                paymentBucketFor(record) === "paid" ? BUTTON_TONES.orange : BUTTON_TONES.primary
                              }`}
                            >
                              {paymentBucketFor(record) === "paid" ? "Mark unpaid" : "Mark paid"}
                            </Button>
                          )}
                          {/* A paid salary can't go back to pending — the backend refuses it. */}
                          {canApprovePayroll && paymentBucketFor(record) !== "paid" && (
                            <Button
                              onClick={() => openRevertDialog(record)}
                              disabled={isApproving}
                              className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 whitespace-nowrap ${BUTTON_TONES.orange}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> Move to Pending
                            </Button>
                          )}
                          </>
                      ) : (
                        !canApprovePayroll && <span className="text-xs text-gray-400">—</span>
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onOpenChange={(open) => {
          setShowDetailsDialog(open);
          if (!open) setSelectedRecord(null);
        }}
      >
        <DialogContent className="max-w-[92%] sm:max-w-xl rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#475569]" />
              Salary Details -{" "}
              {selectedRecord && (
                <PersonAvatar
                  employeeId={selectedRecord.emptID}
                  employeeCode={selectedRecord.employeeID}
                  name={selectedRecord.name}
                  size={28}
                />
              )}
              {selectedRecord?.name}
            </DialogTitle>
            <DialogDescription>
              Read-only salary record details for the selected approval period.
            </DialogDescription>
          </DialogHeader>

          {selectedRecord && (
            <div className="mt-3 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailRow label="Employee ID" value={selectedRecord.employeeID} />
                <DetailRow label="Designation" value={selectedRecord.designation} />
                <DetailRow label="Period" value={`${months.find(m => m.value === selectedRecord.month?.toString().padStart(2, "0"))?.label} ${selectedRecord.year}`} />
                <DetailRow label="Status" value={approvalBucketFor(selectedRecord) === "approved" ? "Approved" : "Pending"} />
                <DetailRow label="Monthly Salary" value={formatCurrency(selectedRecord.monthlySalary || 0)} />
                <DetailRow label="Net Salary" value={formatCurrency(selectedRecord.netSalary || 0)} />
                <DetailRow label="Bonus" value={formatCurrency(selectedRecord.bonus || 0)} />
                <DetailRow label="Extra Pay" value={formatCurrency(selectedRecord.extrapay || 0)} />
                <DetailRow label="Calculation Type" value={selectedRecord.calculationType} />
                <DetailRow label="Payment Mode" value={selectedRecord.mode} />
                <DetailRow
                  label="Payment Status"
                  value={
                    paymentBucketFor(selectedRecord) === "paid"
                      ? `Paid · ${formatIsoDate(selectedRecord.payment?.txn_date)}${
                          selectedRecord.payment?.utr ? ` · ${selectedRecord.payment.utr}` : ""
                        }`
                      : paymentBucketFor(selectedRecord) === "returned"
                      ? `Returned · ${selectedRecord.payment?.return_reason || "transfer came back"}`
                      : "Unpaid"
                  }
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    setSelectedRecord(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm ${BUTTON_TONES.slate}`}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Dialog with Comments */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-green-600" />
              Approve Salary -{" "}
              {selectedRecord && (
                <PersonAvatar
                  employeeId={selectedRecord.emptID}
                  employeeCode={selectedRecord.employeeID}
                  name={selectedRecord.name}
                  size={28}
                />
              )}
              {selectedRecord?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 sm:mt-3 space-y-4">
            {/* Employee Info Summary */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Employee ID:</span>
                <span className="font-medium text-gray-900">{selectedRecord?.employeeID}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Net Salary:</span>
                <span className="font-bold text-emerald-600">{formatCurrency(selectedRecord?.netSalary || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bonus:</span>
                <span className="font-medium text-amber-600">{formatCurrency(selectedRecord?.bonus || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Extra Pay:</span>
                <span className="font-medium text-blue-600">{formatCurrency(selectedRecord?.extrapay || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Period:</span>
                <span className="font-medium text-gray-900">
                  {months.find(m => m.value === selectedRecord?.month?.toString().padStart(2, "0"))?.label} {selectedRecord?.year}
                </span>
              </div>
            </div>

            {/* Comments Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                Approval Comments (Optional)
              </label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder="Enter any comments or notes for this approval..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-sm resize-none"
              />
            </div>

            {/* Export Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800 flex items-center gap-2">
                <FileText className="w-3 h-3" />
                Note: After approval, employee and salary data will be automatically exported.
              </p>
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowApproveDialog(false);
                  setSelectedRecord(null);
                  setApprovalComments("");
                }}
                variant="outline"
                className="flex-1 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApproveWithComments}
                disabled={isApproving || isExporting}
                className={`flex-1 text-sm ${BUTTON_TONES.primary}`}
              >
                {isApproving || isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ThumbsUp className="w-4 h-4 mr-2" />
                )}
                {isApproving ? "Approving..." : isExporting ? "Exporting..." : "Confirm Approval"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move approved salary back to pending */}
      <Dialog
        open={showRevertDialog}
        onOpenChange={(open) => {
          setShowRevertDialog(open);
          if (!open) setSelectedRecord(null);
        }}
      >
        <DialogContent className="max-w-[92%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              Move Salary to Pending
            </DialogTitle>
            <DialogDescription>
              This will remove the approval from {selectedRecord?.name}&apos;s salary and return it to the pending approval queue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowRevertDialog(false)}
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRevertToPending}
              disabled={isApproving}
              className={BUTTON_TONES.orange}
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Confirm Move to Pending
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog with Reason */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <ThumbsDown className="w-5 h-5 text-red-600" />
              Reject Salary - {selectedRecord?.name}
            </DialogTitle>
            <DialogDescription>
              Provide the reason for rejecting this salary record.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 sm:mt-3 space-y-4">
            {/* Employee Info Summary */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Employee ID:</span>
                <span className="font-medium text-gray-900">{selectedRecord?.employeeID}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Net Salary:</span>
                <span className="font-bold text-emerald-600">{formatCurrency(selectedRecord?.netSalary || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bonus:</span>
                <span className="font-medium text-amber-600">{formatCurrency(selectedRecord?.bonus || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Extra Pay:</span>
                <span className="font-medium text-blue-600">{formatCurrency(selectedRecord?.extrapay || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Period:</span>
                <span className="font-medium text-gray-900">
                  {months.find(m => m.value === selectedRecord?.month?.toString().padStart(2, "0"))?.label} {selectedRecord?.year}
                </span>
              </div>
            </div>

            {/* Rejection Reason Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this salary..."
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-sm resize-none"
              />
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowRejectDialog(false);
                  setSelectedRecord(null);
                  setRejectionReason("");
                }}
                variant="outline"
                className="flex-1 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectWithReason}
                disabled={isApproving || !rejectionReason.trim()}
                className={`flex-1 text-sm ${BUTTON_TONES.orange}`}
              >
                {isApproving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ThumbsDown className="w-4 h-4 mr-2" />
                )}
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export bank bulk-upload sheet — asks for the debit account + the
          per-bank constants printed on every row, since none are stored
          anywhere. Same dialog the Expenses page uses for reimbursements. */}
      <BankExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onConfirm={exportApprovedSalariesToCSV}
        exporting={isExporting}
        title="Export Salary Bulk Upload Sheet"
        confirmClassName={BUTTON_TONES.primary}
      />

      {/* Upload Bank Statement — matches the statement's debits to this month's
          approved salaries and marks the confirmed ones paid. */}
      <BankStatementUploadDialog
        open={showStatementDialog}
        onOpenChange={setShowStatementDialog}
        year={selectedYear}
        month={selectedMonth}
        periodLabel={periodLabel}
        onApplied={handleStatementApplied}
        confirmClassName={BUTTON_TONES.primary}
      />

      {/* Mark one salary Paid / Unpaid by hand — cash, cheque, or undoing a wrong match. */}
      <Dialog
        open={paymentRecord !== null}
        onOpenChange={(open) => {
          if (!open && !isSavingPayment) setPaymentRecord(null);
        }}
      >
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              {markingUnpaid ? "Mark Unpaid" : "Mark Paid"} - {paymentRecord?.name}
            </DialogTitle>
            <DialogDescription>
              {markingUnpaid
                ? "The salary goes back to Unpaid. The recorded payment is kept in its history."
                : `Record ${formatCurrency(paymentRecord?.netSalary || 0)} as paid without a bank statement — for cash, a cheque, or a transfer the statement didn't show.`}
            </DialogDescription>
          </DialogHeader>

          {!markingUnpaid && (
            <div className="mt-2 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paid on</label>
                <input
                  type="date"
                  value={paymentForm.paidOn}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, paidOn: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UTR / reference (optional)</label>
                <input
                  value={paymentForm.reference}
                  maxLength={64}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, reference: e.target.value }))}
                  placeholder="e.g. UTR or cheque number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea
                  value={paymentForm.note}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, note: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 sm:gap-3 mt-4">
            <Button
              onClick={() => setPaymentRecord(null)}
              disabled={isSavingPayment}
              variant="outline"
              className="flex-1 text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={savePayment}
              disabled={isSavingPayment}
              className={`flex-1 text-sm ${markingUnpaid ? BUTTON_TONES.orange : BUTTON_TONES.primary}`}
            >
              {isSavingPayment && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {markingUnpaid ? "Mark Unpaid" : "Mark Paid"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalSettings;
